import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { builderApi, type Build } from '@/utils/api-pb';

interface SiteSettingsPanelProps {
  build: Build;
  isSaving: boolean;
  onSave: (patch: { name?: string; slug?: string }) => void;
}

/** Debounce for the availability check — one request per pause, not per keystroke. */
const CHECK_DELAY_MS = 400;

export function SiteSettingsPanel({ build, isSaving, onSave }: SiteSettingsPanelProps) {
  const { t } = useTranslation('builder');
  const [name, setName] = useState(build.name);
  const [slug, setSlug] = useState(build.slug);
  const [slugState, setSlugState] = useState<
    { status: 'idle' | 'checking' } | { status: 'ok' } | { status: 'error'; message: string }
  >({ status: 'idle' });

  useEffect(() => {
    setName(build.name);
    setSlug(build.slug);
    setSlugState({ status: 'idle' });
  }, [build.id, build.name, build.slug]);

  useEffect(() => {
    const trimmed = slug.trim();
    if (!trimmed || trimmed === build.slug) {
      setSlugState({ status: 'idle' });
      return;
    }

    setSlugState({ status: 'checking' });
    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const result = await builderApi.checkSlug(trimmed, build.id);
        if (cancelled) return;
        setSlugState(
          result.available
            ? { status: 'ok' }
            : { status: 'error', message: result.error || '' },
        );
      } catch {
        // A failed check should not block saving — the backend validates again anyway.
        if (!cancelled) setSlugState({ status: 'idle' });
      }
    }, CHECK_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [slug, build.id, build.slug]);

  const nameChanged = name.trim() !== build.name;
  const slugChanged = slug.trim() !== build.slug;
  const canSave =
    !isSaving &&
    (nameChanged || slugChanged) &&
    name.trim().length > 0 &&
    slugState.status !== 'error' &&
    slugState.status !== 'checking';

  return (
    <div className="flex flex-col gap-[16px]">
      <div className="flex flex-col gap-[6px]">
        <Label htmlFor="settings-name">{t('settings.name.label')}</Label>
        <Input
          id="settings-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
        />
      </div>

      <div className="flex flex-col gap-[6px]">
        <Label htmlFor="settings-slug">{t('settings.slug.label')}</Label>
        <Input
          id="settings-slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          placeholder="anna-kovaleva"
          maxLength={40}
        />
        <p
          className={
            'font-segoe text-[13px] ' +
            (slugState.status === 'error'
              ? 'text-[#d13438]'
              : slugState.status === 'ok'
                ? 'text-[color:var(--main-brand,#2197f1)]'
                : 'text-[color:var(--grey-text,#565858)]')
          }
        >
          {slugState.status === 'error'
            ? slugState.message
            : slugState.status === 'ok'
              ? t('settings.slug.available')
              : slugState.status === 'checking'
                ? t('settings.slug.checking')
                : t('settings.slug.hint')}
        </p>
      </div>

      <Button
        onClick={() =>
          onSave({
            ...(nameChanged ? { name: name.trim() } : {}),
            ...(slugChanged ? { slug: slug.trim() } : {}),
          })
        }
        disabled={!canSave}
      >
        {t('settings.save')}
      </Button>
    </div>
  );
}
